/**
 * Phase 1.7 — Pastures module index, v2.
 *
 * Group-by: status (Active / Resting / Closed-or-Empty).
 *
 * Cards are interactive and navigate to /pastures/:id on click;
 * edit/archive buttons stopPropagation so they don't also navigate.
 * The view toggle (grid / list) actually re-renders — previously the
 * list option was read-only.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Fence, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, EmptyState, FilterBar, KpiCard, SectionHeader, ViewToggle, type ViewMode } from '@/components/v2';
import { authFetch } from '@/lib/auth';
import { useUiPrefs } from '@/lib/layout-flag';
import { usePageHeader } from '@/components/v2/layout/Header';
import { AddEntityDialog } from '@/components/v2/dialogs/AddEntityDialog';
import { EditEntityDialog } from '@/components/v2/dialogs/EditEntityDialog';
import { DeleteEntityDialog } from '@/components/v2/dialogs/DeleteEntityDialog';
import { EntityImage } from '@/components/EntityImage';

interface PastureRow {
  id: string;
  ranch_id: string;
  name: string;
  status: 'active' | 'resting' | 'closed' | 'empty';
  acres?: number | null;
  acreage?: number | null;
  last_rotation_at?: string | null;
  last_rotation?: string | null;
  fencing_status?: string | null;
  notes?: string | null;
  animal_count?: number;
}

type Status = 'active' | 'resting' | 'closed';
const STATUS_ORDER: Status[] = ['active', 'resting', 'closed'];
const STATUS_LABEL: Record<Status, string> = {
  active: 'Active',
  resting: 'Resting',
  closed: 'Closed',
};
const STATUS_TONE: Record<Status, string> = {
  active: 'bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark',
  resting: 'bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark',
  closed: 'bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark',
};

const VIEW_KEY = 'pastures';

const FIELDS = [
  { name: 'name', label: 'Name', required: true, placeholder: 'North Pasture' },
  { name: 'acres', label: 'Acres', type: 'number' as const, half: true, placeholder: '40' },
  {
    name: 'status',
    label: 'Status',
    type: 'select' as const,
    half: true,
    defaultValue: 'active',
    options: STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
  },
  { name: 'notes', label: 'Notes', type: 'textarea' as const },
];

const transformPasture = (v: Record<string, string>): Record<string, unknown> => ({
  ...(v.name !== undefined ? { name: v.name } : {}),
  ...(v.acres !== undefined ? { acres: v.acres ? Number(v.acres) : null } : {}),
  ...(v.status !== undefined ? { status: v.status || 'active' } : {}),
  ...(v.notes !== undefined ? { notes: v.notes || null } : {}),
});

function statusOf(row: PastureRow): Status {
  if (row.status === 'empty') return 'closed';
  if (row.status === 'active' || row.status === 'resting' || row.status === 'closed') return row.status;
  return 'active';
}

function acresOf(row: PastureRow): number {
  return row.acres ?? row.acreage ?? 0;
}

function lastRotationOf(row: PastureRow): string | null {
  return row.last_rotation_at ?? row.last_rotation ?? null;
}

export function PasturesPageV2(): JSX.Element {
  const navigate = useNavigate();
  const { prefs, patch } = useUiPrefs();
  const view: ViewMode = prefs.view_modes?.[VIEW_KEY] ?? 'grid';
  const [pastures, setPastures] = useState<PastureRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | Status>('all');
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [editTarget, setEditTarget] = useState<PastureRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PastureRow | null>(null);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  usePageHeader(
    'Pastures',
    <Button
      size="sm"
      onClick={() => setAddOpen(true)}
      className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
    >
      <Plus className="h-4 w-4" />
      Add pasture
    </Button>,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    async function load(): Promise<void> {
      try {
        const data = await authFetch<{ items: PastureRow[] }>('/api/pastures');
        if (!cancelled) setPastures(data.items ?? []);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load pastures');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  const filtered = useMemo<PastureRow[]>(() => {
    const q = search.trim().toLowerCase();
    return pastures.filter((p) => {
      if (q && !p.name.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'all' && statusOf(p) !== statusFilter) return false;
      return true;
    });
  }, [pastures, search, statusFilter]);

  const grouped = useMemo<Record<Status, PastureRow[]>>(() => {
    const out: Record<Status, PastureRow[]> = { active: [], resting: [], closed: [] };
    for (const p of filtered) out[statusOf(p)].push(p);
    return out;
  }, [filtered]);

  const totals = useMemo(() => {
    const active = pastures.filter((p) => statusOf(p) === 'active').length;
    const acres = pastures.reduce((sum, p) => sum + acresOf(p), 0);
    const animals = pastures.reduce((sum, p) => sum + (p.animal_count ?? 0), 0);
    return { active, acres, animals };
  }, [pastures]);

  const setView = (next: ViewMode): void => {
    void patch({ view_modes: { ...(prefs.view_modes ?? {}), [VIEW_KEY]: next } });
  };

  const hasActiveFilters = Boolean(search) || statusFilter !== 'all';
  const clear = (): void => {
    setSearch('');
    setStatusFilter('all');
  };

  const refresh = (): void => setRefreshTick((t) => t + 1);
  const stop = (e: React.MouseEvent): void => e.stopPropagation();

  return (
    <div className="space-y-6">
      <AddEntityDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Add Pasture"
        endpoint="/api/pastures"
        entityType="pasture"
        fields={FIELDS}
        transform={transformPasture}
        onAdded={refresh}
      />
      {editTarget ? (
        <EditEntityDialog
          open={Boolean(editTarget)}
          onOpenChange={(o) => { if (!o) setEditTarget(null); }}
          title={`Edit ${editTarget.name}`}
          endpoint="/api/pastures"
          entityId={editTarget.id}
          fields={FIELDS}
          initialValues={{
            name: editTarget.name,
            acres: String(acresOf(editTarget) || ''),
            status: statusOf(editTarget),
            notes: editTarget.notes ?? '',
          }}
          transform={transformPasture}
          onSaved={refresh}
        />
      ) : null}
      {deleteTarget ? (
        <DeleteEntityDialog
          open={Boolean(deleteTarget)}
          onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
          endpoint="/api/pastures"
          entityId={deleteTarget.id}
          entityName={deleteTarget.name}
          onDeleted={refresh}
        />
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          label="Pastures"
          icon={Fence}
          primary={{ label: 'Active', value: totals.active }}
          secondary={{ label: 'Total', value: pastures.length }}
        />
        <KpiCard
          label="Acreage"
          primary={{ label: 'Total acres', value: totals.acres.toLocaleString() }}
        />
        <KpiCard
          label="Stock"
          primary={{ label: 'Animals on pasture', value: totals.animals }}
        />
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search pastures' }}
        filters={[
          <FilterSelect
            key="status"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as 'all' | Status)}
            options={[
              { value: 'all', label: 'All statuses' },
              ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
            ]}
          />,
        ]}
        actions={<ViewToggle value={view} onChange={setView} />}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
      />

      {error ? (
        <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Fence}
          title={hasActiveFilters ? 'No matches' : 'No pastures yet'}
          description={
            hasActiveFilters
              ? 'Try clearing filters.'
              : 'Add a pasture to track rotation, acreage, and stocking density.'
          }
          action={
            hasActiveFilters ? (
              <Button onClick={clear} variant="outline">Clear filters</Button>
            ) : (
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" />
                Add pasture
              </Button>
            )
          }
        />
      ) : (
        STATUS_ORDER.filter((s) => grouped[s].length > 0).map((status) => (
          <SectionHeader key={status} title={STATUS_LABEL[status]} count={grouped[status].length} collapsible>
            {view === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {grouped[status].map((p) => {
                  const lr = lastRotationOf(p);
                  return (
                    <Card
                      key={p.id}
                      interactive
                      onClick={() => navigate(`/pastures/${p.id}`)}
                      thumbnail={<EntityImage entityType="pasture" entityId={p.id} alt={p.name} />}
                      title={p.name}
                      subtitle={lr ? `Rotated ${lr}` : 'Never rotated'}
                      badge={
                        <span className={`rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[statusOf(p)]}`}>
                          {STATUS_LABEL[statusOf(p)]}
                        </span>
                      }
                      footer={
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-xs">
                            {acresOf(p) ? `${acresOf(p)} acres` : '\u2014'}
                            {p.animal_count ? ` \u00b7 ${p.animal_count} animals` : ''}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { stop(e); setEditTarget(p); }}
                              aria-label={`Edit ${p.name}`}
                              className="h-7 w-7"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { stop(e); setDeleteTarget(p); }}
                              aria-label={`Archive ${p.name}`}
                              className="h-7 w-7 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border bg-surface dark:bg-surface-dark dark:border-border">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-surface-raised text-left dark:bg-surface-raised-dark">
                    <tr>
                      <Th>Name</Th>
                      <Th>Acres</Th>
                      <Th>Last rotated</Th>
                      <Th>Status</Th>
                      <Th className="text-right">Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[status].map((p) => (
                      <tr
                        key={p.id}
                        className="cursor-pointer border-b border-border last:border-0 hover:bg-surface-raised dark:hover:bg-surface-raised-dark"
                        onClick={() => navigate(`/pastures/${p.id}`)}
                      >
                        <Td className="font-medium">{p.name}</Td>
                        <Td className="font-mono text-xs">{acresOf(p) || '\u2014'}</Td>
                        <Td className="text-muted-foreground">{lastRotationOf(p) ?? '\u2014'}</Td>
                        <Td>
                          <span className={`inline-flex rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[statusOf(p)]}`}>
                            {STATUS_LABEL[statusOf(p)]}
                          </span>
                        </Td>
                        <Td className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setEditTarget(p); }} aria-label={`Edit ${p.name}`} className="h-7 w-7">
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setDeleteTarget(p); }} aria-label={`Archive ${p.name}`} className="h-7 w-7 text-destructive hover:text-destructive">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionHeader>
        ))
      )}
    </div>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2 text-sm shadow-sm dark:bg-surface-dark dark:border-border"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <th className={`px-4 py-2 font-mono text-2xs uppercase tracking-wider text-muted-foreground ${className ?? ''}`}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <td className={`px-4 py-3 ${className ?? ''}`}>{children}</td>;
}
